import { getUserTagValueOffEvent, Tags, IEvent } from "@util/event-tag";
import { IGraphEntityError } from "@util/graphql";

import { articleUrls } from "@constants/help-center";
import React from "react";

export interface IShadowCalendarSettings {
  __typename: "ShadowCalendarSettings";
  shadowCalendarId: string | null;
}

export type IShadowCalendarSettingsErrorable<SCS extends IShadowCalendarSettings> =
  | IGraphEntityError
  | SCS;

export interface IEventCategoryColoring {
  __typename: "EventCategoryColoring";
  active: boolean;
  isUserSet: boolean;
  eventCategory: string;
  googleColorKey: string | null;
}

export interface IEventColoringSettings<ECC extends IEventCategoryColoring> {
  __typename: "EventColoringSettings";
  eventCategoryColorings: ECC[] | null;
}

export type IEventColoringSettingsErrorable<
  ECC extends IEventCategoryColoring,
  ECS extends IEventColoringSettings<ECC>
> = IGraphEntityError | ECS;

export interface ICalColorSet {
  background: string;
  foreground: string;
  name: string;
}

/**
 * String values map to values provided by services.
 */
export enum EventColorCategory {
  AdHoc = "AdHoc",
  External = "External",
  FocusTime = "FocusTime",
  IndividualHold = "IndividualHold",
  Holiday = "Holiday",
  OneOnOne = "OneOnOne",
  Other = "Other",
  PaceSetting = "PaceSetting",
  OutOfOffice = "OutOfOffice",
}

export const EventColorCategorySort: { [key: string]: number } = {
  External: 1,
  OneOnOne: 2,
  FocusTime: 3,
  IndividualHold: 4,
  Holiday: 5,
  PaceSetting: 6,
  AdHoc: 7,
  OutOfOffice: 8,
  Other: 9,
};

/**
 * Default event colors pulled from Google calendar API.
 */
export const calendarColors: { [key: string]: ICalColorSet } = {
  1: {
    background: "#a4bdfc",
    foreground: "#7986CB",
    name: "Lavender",
  },
  2: {
    background: "#7ae7bf",
    foreground: "#33B679",
    name: "Sage",
  },
  3: {
    background: "#dbadff",
    foreground: "#8E24AA",
    name: "Grape",
  },
  4: {
    background: "#ff887c",
    foreground: "#E67C73",
    name: "Flamingo",
  },
  5: {
    background: "#fbd75b",
    foreground: "#F6BF26",
    name: "Banana",
  },
  6: {
    background: "#ffb878",
    foreground: "#F4511E",
    name: "Tangerine",
  },
  7: {
    background: "#46d6db",
    foreground: "#039BE5",
    name: "Peacock",
  },
  8: {
    background: "#e1e1e1",
    foreground: "#616161",
    name: "Graphite",
  },
  9: {
    background: "#5484ed",
    foreground: "#3F51B5",
    name: "Blueberry",
  },
  10: {
    background: "#51b749",
    foreground: "#0B8043",
    name: "Basil",
  },
  11: {
    background: "#dc2127",
    foreground: "#D50000",
    name: "Tomato",
  },
};

/**
 * This is the default set to save from the suggestion tip card.
 */
export const defaultSet = [
  {
    eventCategory: EventColorCategory.External,
    active: true,
    googleColorKey: "4", // flamingo
    isUserSet: true,
  },
  {
    eventCategory: EventColorCategory.IndividualHold,
    active: true,
    googleColorKey: "1", // lavender
    isUserSet: true,
  },
  {
    eventCategory: EventColorCategory.FocusTime,
    active: true,
    googleColorKey: "8", // graphite
    isUserSet: true,
  },
  {
    eventCategory: EventColorCategory.OneOnOne,
    active: true,
    googleColorKey: "2", // sage
    isUserSet: true,
  },
  {
    eventCategory: EventColorCategory.Holiday,
    active: true,
    googleColorKey: "5", // banana
    isUserSet: true,
  },
];

export function isValidCategory(eventCategory: string) {
  return !!Object.values(EventColorCategory).includes(eventCategory as EventColorCategory);
}

export function filterAndSortCategories<ECC extends IEventCategoryColoring>(
  eventColoringSettings?: IGraphEntityError | IEventColoringSettings<ECC>,
) {
  if (
    !eventColoringSettings ||
    eventColoringSettings.__typename === "GraphEntityError" ||
    !eventColoringSettings.eventCategoryColorings
  ) {
    return [];
  }
  return eventColoringSettings.eventCategoryColorings
    .map((ecc) => {
      const sortValue = EventColorCategorySort[ecc.eventCategory];
      const valid = !!sortValue && isValidCategory(ecc.eventCategory);
      return {
        sortValue,
        valid,
        ecc,
      };
    })
    .filter((eccObj) => {
      // Very noisy warn. Commenting out for now until Danyaal is back to discuss it.
      // if (!eccObj.valid) {
      //   console.warn(
      //     `coloring category ${eccObj.ecc.eventCategory} is unaccounted for; front-end will not display it`,
      //   );
      // }
      return eccObj.valid;
    })
    .sort((a, b) => a.sortValue - b.sortValue)
    .map((eccObj) => eccObj.ecc);
}

export function getCategoryLabels(
  eventCategory: string,
): { label: string; description: React.ReactNode } {
  switch (eventCategory) {
    case EventColorCategory.AdHoc:
      return {
        label: "Ad-hoc",
        description: "Non-recurring meetings that pop-up in the course of a week",
      };
    case EventColorCategory.External:
      return {
        label: "External attendees",
        description: "Any meeting with one or more external attendees",
      };
    case EventColorCategory.FocusTime:
      return {
        label: "Focus Time",
        description: "Calendar blocks that are for Focus Time",
      };
    case EventColorCategory.IndividualHold:
      return {
        label: "Hold",
        description: "Calendar events that look like non-meeting holds",
      };
    case EventColorCategory.OneOnOne:
      return {
        label: "One-on-one",
        description: "Meetings with you and one other internal attendee",
      };
    case EventColorCategory.PaceSetting:
      return {
        label: "Team sync",
        description: "All-hands, project syncs, etc.",
      };
    case EventColorCategory.OutOfOffice:
      return {
        label: "Out of office / unavailable",
        description: "Calendar blocks for time out of office",
      };
    case EventColorCategory.Holiday:
      return {
        label: "Holiday",
        description: (
          <React.Fragment>
            Calendar for company or team holidays. Sets attendees as OOO.
            <a href={articleUrls.holiday} target="_blank" rel="noreferrer">
              Learn more
            </a>
          </React.Fragment>
        ),
      };
    case EventColorCategory.Other:
    default:
      return {
        label: "Other",
        description: "Everything else",
      };
  }
}

export function getColorFromSetting(eventCategoryColoring: IEventCategoryColoring) {
  return (
    (eventCategoryColoring.active &&
      eventCategoryColoring.googleColorKey &&
      calendarColors[eventCategoryColoring.googleColorKey] &&
      calendarColors[eventCategoryColoring.googleColorKey].foreground) ||
    null
  );
}

export function getColor<ECC extends IEventCategoryColoring>(
  selectedEventCategory: string,
  eventColoringSettings: IEventColoringSettings<ECC>,
  isSyncedFocusTime?: boolean,
) {
  const { eventCategoryColorings } = eventColoringSettings;

  if (!eventCategoryColorings) {
    return null;
  }

  const selectedSetting = eventCategoryColorings.find(
    (ecc) => ecc.eventCategory === selectedEventCategory,
  );
  if (!selectedSetting) {
    return null;
  }
  // for focus time smart holds, if user has not ever set color, use the default color!
  if (isSyncedFocusTime && !selectedSetting.isUserSet) {
    const graphite = calendarColors["8"];
    return graphite.foreground;
  }
  return getColorFromSetting(selectedSetting);
}

export function getGoogleColorKey<ECC extends IEventCategoryColoring>(
  selectedEventCategory: string,
  eventColoringSettings: IEventColoringSettings<ECC>,
) {
  const { eventCategoryColorings } = eventColoringSettings;

  if (!eventCategoryColorings) {
    return null;
  }

  const selectedSetting = eventCategoryColorings.find(
    (ecc) => ecc.eventCategory === selectedEventCategory,
  );
  if (!selectedSetting) {
    return null;
  }

  return selectedSetting.googleColorKey;
}

export function getCurrentSettings<
  ECC extends IEventCategoryColoring,
  ECS extends IEventColoringSettings<ECC>
>(eventColoringSettings?: IEventColoringSettingsErrorable<ECC, ECS>) {
  return filterAndSortCategories(eventColoringSettings).map((ecc) => {
    const labels = getCategoryLabels(ecc.eventCategory);
    const renderColor = getColorFromSetting(ecc);
    return { labels, renderColor, ...ecc };
  });
}

export function getExternalSetting<
  ECC extends IEventCategoryColoring,
  ECS extends IEventColoringSettings<ECC>
>(eventColoringSettings: IEventColoringSettingsErrorable<ECC, ECS>): ECC | undefined {
  if (eventColoringSettings.__typename === "GraphEntityError") {
    return undefined;
  }

  return getSettingByCategory(eventColoringSettings, EventColorCategory.External);
}

export function getEventColor<ECC extends IEventCategoryColoring, E extends IEvent>(
  event: E,
  colorSettings?: IEventColoringSettings<ECC>,
  userId?: string,
  isSyncedFocusTime?: boolean,
  defaultColor = "#049BE5",
) {
  if (!colorSettings || !userId) {
    return defaultColor;
  }

  const colorState = getUserTagValueOffEvent(event, Tags.EventColoringCategory, userId);
  let eventCategory;

  if (colorState) {
    eventCategory = colorState.value as EventColorCategory;
  } else {
    eventCategory = EventColorCategory.Other;
  }

  return getColor(eventCategory, colorSettings, isSyncedFocusTime) || defaultColor;
}

export function getEventColorGoogleKey<ECC extends IEventCategoryColoring, E extends IEvent>(
  event: E,
  colorSettings?: IEventColoringSettings<ECC>,
  userId?: string,
) {
  if (!colorSettings || !userId) {
    return null;
  }

  const colorState = getUserTagValueOffEvent(event, Tags.EventColoringCategory, userId);
  let eventCategory;

  if (colorState) {
    eventCategory = colorState.value as EventColorCategory;
  } else {
    eventCategory = EventColorCategory.Other;
  }

  return getGoogleColorKey(eventCategory, colorSettings);
}

export function getOneOnOneSetting<
  ECC extends IEventCategoryColoring,
  ECS extends IEventColoringSettings<ECC>
>(eventColoringSettings: IEventColoringSettingsErrorable<ECC, ECS>) {
  if (eventColoringSettings.__typename === "GraphEntityError") {
    return undefined;
  }
  return getSettingByCategory(eventColoringSettings, EventColorCategory.OneOnOne);
}

export function getSettingByCategory<
  ECC extends IEventCategoryColoring,
  ECS extends IEventColoringSettings<ECC>
>(eventColoringSettings: ECS, category: EventColorCategory): ECC | undefined {
  if (!eventColoringSettings.eventCategoryColorings) {
    return;
  }

  return eventColoringSettings.eventCategoryColorings.find((ecc) => ecc.eventCategory === category);
}

//////////////////////
// Default Handling //
//////////////////////

/**
 * Merges remote settings, if any, with defaults.
 * @param eventColoringSettings
 */
export function getMergedDefaultSet<
  ECC extends IEventCategoryColoring,
  ECS extends IEventColoringSettings<ECC>
>(eventColoringSettings?: IEventColoringSettingsErrorable<ECC, ECS>) {
  return defaultSet.map((defaultSetting) => {
    let googleColorKey = defaultSetting.googleColorKey;
    let active = defaultSetting.active;
    if (!!eventColoringSettings && eventColoringSettings.__typename === "EventColoringSettings") {
      const remoteSetting = getSettingByCategory(
        eventColoringSettings,
        defaultSetting.eventCategory,
      );
      googleColorKey =
        (remoteSetting && remoteSetting.active && remoteSetting.googleColorKey) ||
        defaultSetting.googleColorKey;
      active =
        remoteSetting && remoteSetting.active !== undefined
          ? remoteSetting.active
          : defaultSetting.active;
    }
    const label = getCategoryLabels(defaultSetting.eventCategory).label;
    const color = calendarColors[googleColorKey].foreground;
    return { color, label, active };
  });
}

export function resetEventColoringSettings<
  ECC extends IEventCategoryColoring,
  ECS extends IEventColoringSettings<ECC>
>(eventColoringSettings?: IEventColoringSettingsErrorable<ECC, ECS>) {
  return filterAndSortCategories(eventColoringSettings).map((ecc) => {
    const maybeDefaultSetting = defaultSet.find((ds) => ds.eventCategory === ecc.eventCategory);
    return {
      eventCategory: ecc.eventCategory,
      active: ecc.active,
      googleColorKey: maybeDefaultSetting ? maybeDefaultSetting.googleColorKey : undefined,
    };
  });
}

export function getEventCategory<E extends IEvent>(event: E, userId: string) {
  const colorState = getUserTagValueOffEvent(event, Tags.EventColoringCategory, userId);

  if (colorState) {
    return colorState.value as EventColorCategory;
  } else {
    return EventColorCategory.Other;
  }
}
